'use client';

import * as React from 'react';
import { useIsMobile } from '../../hooks/use-mobile';
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { t } from '@transifex/native';

interface BreadcrumbEntry {
  label: string;
  href?: string;
}

interface PageNavigationProps {
  title: React.ReactElement | string;
  subtitle?: React.ReactElement | string;
  breadcrumbs?: Array<BreadcrumbEntry>;
  additional?: React.ReactNode;
}

const COLLAPSE_THRESHOLD = 6;

function PageNavigation({
  breadcrumbs = [],
  title,
  subtitle,
  additional
}: PageNavigationProps) {
  const isMobile = useIsMobile();
  const shouldCollapse =
    breadcrumbs.length > 2 &&
    (isMobile || breadcrumbs.length > COLLAPSE_THRESHOLD);
  const breadcrumbsWithHome = [
    {
      label: t('Home'),
      href: '/'
    },
    ...breadcrumbs
  ];

  return (
    <nav className="w-full">
      <div className="mx-auto flex w-full max-w-[1536px] flex-col gap-4 px-4 py-6 sm:px-6 lg:px-8">
        <Breadcrumb>
          <BreadcrumbList>
            {shouldCollapse ? (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink href={breadcrumbsWithHome.at(0)!.href}>
                    {breadcrumbsWithHome.at(0)!.label}
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <DropdownMenu>
                    <DropdownMenuTrigger className="flex items-center gap-1">
                      <BreadcrumbEllipsis className="h-4 w-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {breadcrumbsWithHome.slice(1, -1).map((crumb) => (
                        <DropdownMenuItem key={crumb.label}>
                          <BreadcrumbLink href={crumb.href}>
                            {crumb.label}
                          </BreadcrumbLink>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    {breadcrumbsWithHome.at(-1)!.label}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </>
            ) : (
              breadcrumbsWithHome.map((crumb, index) => (
                <React.Fragment key={crumb.label}>
                  <BreadcrumbItem>
                    {index === breadcrumbsWithHome.length - 1 ? (
                      <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink href={crumb.href}>
                        {crumb.label}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {index < breadcrumbsWithHome.length - 1 && (
                    <BreadcrumbSeparator />
                  )}
                </React.Fragment>
              ))
            )}
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex justify-between gap-2">
          <div className="flex flex-col gap-2">
            <h1 className="font-bold text-2xl text-foreground md:text-3xl">
              {title}
            </h1>
            {subtitle && (
              <p className="text-muted-foreground text-sm">{subtitle}</p>
            )}
          </div>
          {additional}
        </div>
      </div>
    </nav>
  );
}

export { PageNavigation };
export type { PageNavigationProps, BreadcrumbEntry };
